import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiClock, FiBriefcase, FiDollarSign, FiAlertCircle, FiDownload, FiPrinter } from "react-icons/fi";
import html2pdf from "html2pdf.js";
import { graphqlRequest } from "../../../../utils/graphqlRequest";

const GET_WORK_ENTRIES = `
  query ContractorWorkEntries($contractorId: String) {
    contractorWorkEntries(contractorId: $contractorId) {
      id
      date
      status
      source
      workDescription
      hours
      rate
      amount
      project { id name }
    }
  }
`;

const GET_WORK_TRACKING = `
  query ContractorWorkTracking($startDate: String, $endDate: String) {
    contractorWorkTracking(startDate: $startDate, endDate: $endDate) {
      contractors {
        contractorId
        payType
        payRate
        shifts {
          date
          duration
          hours
          approvalStatus
        }
      }
    }
  }
`;

const GET_PAYOUT_HISTORY = `
  query ContractorPayouts($contractorIds: [String!]) {
    contractorPayouts(contractorIds: $contractorIds) {
      id
      periodKey
      periodStartDate
      periodEndDate
      reference
      subtotal
      gst
      totalPayable
      payType
      status
      paidAt
      createdAt
    }
  }
`;

const fmtCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const TABS = ["Work Logs", "Payment History", "Statements"];

const ContractorPayoutPortal = ({ row, periodLabel, isPaid, paidAt, paymentMethod = 'BANK', onClose }) => {
  const [activeTab, setActiveTab] = useState("Work Logs");
  const [workLogs, setWorkLogs] = useState([]);
  const [payHistory, setPayHistory] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const statementRef = useRef(null);

  const contractorId = row?.contractorId;

  const fetchWorkLogs = useCallback(async () => {
    if (!contractorId) {
      setWorkLogs([]);
      return;
    }
    setLoadingLogs(true);
    try {
      // Fetch both sources in parallel; tolerate tracking failures
      const [entriesResult, trackingResult] = await Promise.allSettled([
        graphqlRequest(GET_WORK_ENTRIES, { contractorId }),
        graphqlRequest(GET_WORK_TRACKING, {}),
      ]);

      const entries = entriesResult.status === "fulfilled"
        ? (entriesResult.value?.contractorWorkEntries || [])
        : [];

      // Date-level dedup: skip tracking shifts for dates already in work entries
      const coveredDates = new Set(entries.map((e) => e.date).filter(Boolean));

      // Find this contractor's tracking data and synthesise timesheet rows
      const trackingContractors = trackingResult.status === "fulfilled"
        ? (trackingResult.value?.contractorWorkTracking?.contractors || [])
        : [];
      const contractorTracking = trackingContractors.find(
        (c) => c.contractorId === contractorId
      );
      const timesheetLogs = contractorTracking
        ? (contractorTracking.shifts || [])
            .filter(
              (s) =>
                s?.date &&
                !coveredDates.has(s.date) &&
                String(s.approvalStatus || "").toUpperCase() === "APPROVED"
            )
            .map((s, idx) => {
              const hours = Number(s.hours || 0);
              const rate = Number(contractorTracking.payRate || 0);
              const normalizedPayType = String(contractorTracking.payType || "").toLowerCase().replace(/[_\s-]+/g, "-");
              const effectivePayType = normalizedPayType || (hours > 0 ? "pay-per-hour" : "pay-per-project");
              const amount = effectivePayType.includes("project") ? rate : hours * rate;
              return {
                id: `ts-portal-${contractorId}-${s.date}-${idx}`,
                date: s.date,
                status: "APPROVED",
                source: "timesheet",
                workDescription: s.duration
                  ? `Timesheet (${s.duration})`
                  : "Timesheet Entry",
                hours,
                rate,
                amount: Number(amount.toFixed(2)),
                project: null,
              };
            })
        : [];

      const allLogs = [...entries, ...timesheetLogs].sort(
        (a, b) => String(b.date).localeCompare(String(a.date))
      );
      setWorkLogs(allLogs);
    } catch { setWorkLogs([]); }
    finally { setLoadingLogs(false); }
  }, [contractorId]);

  const fetchPayHistory = useCallback(async () => {
    if (!contractorId) {
      setPayHistory([]);
      return;
    }
    setLoadingHistory(true);
    try {
      const data = await graphqlRequest(GET_PAYOUT_HISTORY, { contractorIds: [contractorId] });
      setPayHistory(data?.contractorPayouts || []);
    } catch { setPayHistory([]); }
    finally { setLoadingHistory(false); }
  }, [contractorId]);

  useEffect(() => {
    fetchWorkLogs();
    fetchPayHistory();
  }, [fetchPayHistory, fetchWorkLogs]);

  const handleDownloadPdf = async () => {
    if (!statementRef.current) return;
    const safeName = String(row.fullName || "contractor").trim().replace(/\s+/g, "-").toLowerCase();
    await html2pdf()
      .set({
        margin: [8, 8, 8, 8],
        filename: `${row.reference}-${safeName}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(statementRef.current)
      .save();
  };

  // ── Summary metrics ──────────────────────────────────────────────────────
  const now = new Date();
  const thisMonthLogs = workLogs.filter((e) => {
    const d = new Date(e.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const hoursThisMonth = thisMonthLogs.reduce((s, e) => s + Number(e.hours || 0), 0);
  const projectsCompleted = new Set(
    workLogs.filter((e) => String(e.status || "").toLowerCase() === "approved" && e.project?.id)
      .map((e) => e.project.id)
  ).size;
  const earningsThisMonth = thisMonthLogs
    .filter((e) => String(e.status || "").toLowerCase() === "approved")
    .reduce((s, e) => s + Number(e.amount || 0), 0);
  const pendingPayout = row.totalPayable || 0;

  return (
    <div className="cportal-wrap">
      {/* ── Page Title ── */}
      <div className="cportal-title-row">
        <div>
          <h2 className="cportal-title">Contractor Portal</h2>
          <p className="cportal-subtitle">View your work logs and payment history</p>
        </div>
        <button className="cportal-back-btn" onClick={onClose}>← Back to Payout</button>
      </div>

      {/* ── Summary Cards ── */}
      <div className="cportal-cards">
        <div className="cportal-card">
          <div className="cportal-card-icon green"><FiClock size={18} /></div>
          <div>
            <div className="cportal-card-label">Hours This Month</div>
            <div className="cportal-card-value">{Number(hoursThisMonth.toFixed(1))}</div>
          </div>
        </div>
        <div className="cportal-card">
          <div className="cportal-card-icon purple"><FiBriefcase size={18} /></div>
          <div>
            <div className="cportal-card-label">Projects Completed</div>
            <div className="cportal-card-value">{projectsCompleted}</div>
          </div>
        </div>
        <div className="cportal-card">
          <div className="cportal-card-icon blue"><FiDollarSign size={18} /></div>
          <div>
            <div className="cportal-card-label">Earnings This Month</div>
            <div className="cportal-card-value">{fmtCurrency(earningsThisMonth)}</div>
          </div>
        </div>
        <div className="cportal-card">
          <div className="cportal-card-icon orange"><FiAlertCircle size={18} /></div>
          <div>
            <div className="cportal-card-label">Pending Payout</div>
            <div className="cportal-card-value">{fmtCurrency(pendingPayout)}</div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="cportal-tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`cportal-tab${activeTab === tab ? " active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="cportal-content">

        {/* ════ WORK LOGS ════ */}
        {activeTab === "Work Logs" && (
          <>
            {loadingLogs ? (
              <div className="cportal-state">Loading work logs...</div>
            ) : workLogs.length === 0 ? (
              <div className="cportal-state">No work entries found.</div>
            ) : (
              <table className="cportal-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Project</th>
                    <th>Description</th>
                    <th>Hours</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {workLogs.map((e) => {
                    const statusStr = String(e.status || "").toLowerCase();
                    const isApproved = statusStr === "approved";
                    return (
                      <tr key={e.id}>
                        <td>{fmtDate(e.date)}</td>
                        <td>{e.project?.name || (String(e.source || "") === "timesheet" ? "Timesheet" : "—")}</td>
                        <td>{e.workDescription || "—"}</td>
                        <td>{Number(e.hours || 0) > 0 ? `${e.hours}h` : "—"}</td>
                        <td>{fmtCurrency(e.amount)}</td>
                        <td>
                          <span className={`cportal-status ${isApproved ? "approved" : "draft"}`}>
                            {isApproved ? "Approved" : "Draft"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* ════ PAYMENT HISTORY ════ */}
        {activeTab === "Payment History" && (
          <>
            {loadingHistory ? (
              <div className="cportal-state">Loading payment history...</div>
            ) : payHistory.length === 0 ? (
              <div className="cportal-state">No payment records found.</div>
            ) : (
              <table className="cportal-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Period</th>
                    <th>Pay Type</th>
                    <th>Subtotal</th>
                    <th>GST</th>
                    <th>Total Payable</th>
                    <th>Status</th>
                    <th>Paid On</th>
                  </tr>
                </thead>
                <tbody>
                  {payHistory.map((p) => {
                    const paid = p.status === "paid";
                    return (
                      <tr key={p.id}>
                        <td className="cportal-ref">{p.reference}</td>
                        <td>{fmtDate(p.periodStartDate)} – {fmtDate(p.periodEndDate)}</td>
                        <td>{p.payType}</td>
                        <td>{fmtCurrency(p.subtotal)}</td>
                        <td>{fmtCurrency(p.gst)}</td>
                        <td>{fmtCurrency(p.totalPayable)}</td>
                        <td>
                          <span className={`cportal-status ${paid ? "approved" : "draft"}`}>
                            {paid ? "Paid" : "Pending"}
                          </span>
                        </td>
                        <td>{paid ? fmtDate(p.paidAt) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* ════ STATEMENTS ════ */}
        {activeTab === "Statements" && (
          <>
            <div className="cportal-stmt-actions">
              <button className="payout-ghost-btn" onClick={() => window.print()}>
                <FiPrinter size={14} /> Print
              </button>
              <button className="payout-primary-btn" onClick={handleDownloadPdf}>
                <FiDownload size={14} /> Download PDF
              </button>
            </div>

            <div className="payout-statement-card" ref={statementRef}>
              <div className="payout-company-row">
                <div>
                  <div className="payout-company-logo">{row.initials}</div>
                  <div className="payout-company-name">{row.businessName}</div>
                  <div className="payout-company-meta">ABN: {row.abnNumber}</div>
                  <div className="payout-company-meta">BSB: {row.bsb} | Account: {row.accountNumber}</div>
                </div>
                <div className="payout-statement-meta">
                  <div className="meta-title">{isPaid ? "Payment Statement" : "Work Estimate"}</div>
                  <div className="meta-row">Period: {periodLabel || "—"}</div>
                  <div className="meta-row">Reference: {row.reference}</div>
                </div>
              </div>

              <div className="payout-contractor-block">
                <div className="label">CONTRACTOR DETAILS</div>
                <div className="value">{row.fullName}</div>
                <div className="meta">ABN: {row.abnNumber}</div>
                <div className="meta">BSB: {row.bsb} | Account: {row.accountNumber}</div>
                <div className="meta" style={{ marginTop: 4 }}>
                  Payment Mode:{" "}
                  <span style={{
                    display: "inline-block",
                    padding: "1px 8px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    background: paymentMethod === "CASH" ? "#fef9c3" : "#dbeafe",
                    color: paymentMethod === "CASH" ? "#92400e" : "#1d4ed8",
                  }}>
                    {paymentMethod === "CASH" ? "Cash" : "Bank Transfer"}
                  </span>
                </div>
              </div>

              {(row.hourlyLineItems || []).length > 0 && (
                <>
                  <div className="payout-section-heading">Hourly Work</div>
                  <table className="payout-statement-table">
                    <thead>
                      <tr><th>Description</th><th>Hours</th><th>Rate</th><th>Amount</th></tr>
                    </thead>
                    <tbody>
                      {row.hourlyLineItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td>{item.hours ? `${item.hours}h` : "—"}</td>
                          <td className="right">{fmtCurrency(item.rate)}/hr</td>
                          <td className="right">{fmtCurrency(item.amount)}</td>
                        </tr>
                      ))}
                      <tr className="payout-section-subtotal">
                        <td colSpan="3"><strong>Hourly Subtotal</strong></td>
                        <td className="right"><strong>{fmtCurrency(row.hourlySubtotal)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}

              {(row.projectLineItems || []).length > 0 && (
                <>
                  <div className="payout-section-heading">Project Work</div>
                  <table className="payout-statement-table">
                    <thead>
                      <tr><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr>
                    </thead>
                    <tbody>
                      {row.projectLineItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td>1</td>
                          <td className="right">{fmtCurrency(item.rate)}</td>
                          <td className="right">{fmtCurrency(item.amount)}</td>
                        </tr>
                      ))}
                      <tr className="payout-section-subtotal">
                        <td colSpan="3"><strong>Project Subtotal</strong></td>
                        <td className="right"><strong>{fmtCurrency(row.projectSubtotal)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}

              <div className="payout-gst-grid">
                <div className="gst-head">GST REGISTERED (10%)</div>
                <div className="gst-col-head">Percent</div>
                <div className="gst-col-head">Amount</div>
                <div className="gst-row-label">{row.includesGst ? "GST Exclusive" : "GST Inclusive"}</div>
                <div className="gst-row-percent">{row.includesGst ? "10%" : "0%"}</div>
                <div className="gst-row-amount">{fmtCurrency(row.gst)}</div>
              </div>

              <div className="payout-totals">
                <div><span>Subtotal</span><strong>{fmtCurrency(row.subtotal)}</strong></div>
                {row.includesGst && (
                  <div><span>GST (10%)</span><strong>{fmtCurrency(row.gst)}</strong></div>
                )}
                <div className="big"><span>Total Payable</span><strong>{fmtCurrency(row.totalPayable)}</strong></div>
              </div>

              <div className="payout-statement-footer">
                <div>
                  <div className="tiny-label">PAYMENT DATE</div>
                  <div className="tiny-value">{paidAt ? new Date(paidAt).toLocaleDateString("en-AU") : "—"}</div>
                </div>
                <div>
                  <div className="tiny-label">PAYMENT MODE</div>
                  <div className="tiny-value" style={{ fontWeight: 700, color: paymentMethod === "CASH" ? "#92400e" : "#1d4ed8" }}>
                    {paymentMethod === "CASH" ? "Cash" : "Bank Transfer"}
                  </div>
                </div>
                <div>
                  <div className="tiny-label">REFERENCE ID</div>
                  <div className="tiny-value">{row.reference}</div>
                </div>
              </div>

              <div className="payout-statement-note">
                {isPaid ? "This payment was recorded by admin." : "This estimate is not a payment receipt. Refer to approved claims for a remittance."}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContractorPayoutPortal;
